<template>
  <div :class="{ 'utm-report-embedded': injectedReportData && isUtmTrends }">
    <!-- Bar Chart (only for utm_table, not utm_trends — which already shows a line chart above) -->
    <template v-if="!isUtmTrends">
      <div class="card main-chart p-3 mb-0" style="height: 340px;" v-if="chartData && !loading">
        <Bar :data="chartData" :options="chartOptions" style="height: 100%; position: relative; width: 100%;" />
      </div>
      <div class="card main-chart p-3 mb-0 d-flex align-items-center justify-content-center" style="height: 340px;" v-else-if="loading">
        <div class="spinner-border text-secondary" role="status"></div>
      </div>
    </template>

    <!-- utm_trends already renders _chart.vue above (with its own date range + warning);
         only show it here for utm_table, which has no other date control. -->
    <HardcodedDateWarning v-if="!isUtmTrends && !injectedReportData" class="mt-3" />

    <!-- Table -->
    <div class="rb-trends-table-wrapper mt-3" :class="{ 'rb-compact': injectedReportData }">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="rb-trends-title mb-0">
          <i class="fas fa-tags me-2 text-primary" v-if="!injectedReportData"></i>
          {{ $t('ReportBuilder.utm.title') }}
        </h5>
      </div>

      <div v-if="limitReached" class="alert alert-info py-2 px-3 mb-3" style="font-size: 13px;">
        <i class="fas fa-info-circle me-1"></i>
        {{ $t('ReportBuilder.utm.rowLimitReached', { limit: reportData.source_request.row_limit }) }}
      </div>

      <div class="rb-table-container">
        <el-table
          v-if="displayRows.length > 0 || loading"
          :data="displayRows"
          style="width: 100%"
          header-cell-class-name="rb-table-header"
          :cell-style="utmCellStyle"
          v-loading="loading && !displayRows.length"
        >
          <!-- Visibility Toggle for trends chart -->
          <el-table-column v-if="isUtmTrends" :width="injectedReportData ? 36 : 50" align="center">
            <template #default="scope">
              <el-checkbox
                v-if="utmColorMap[utmRowKey(scope.row)]"
                :size="injectedReportData ? 'small' : 'default'"
                :model-value="!hiddenDatasets.includes(utmRowKey(scope.row))"
                @change="toggleVisibility(utmRowKey(scope.row))"
              />
            </template>
          </el-table-column>

          <el-table-column :min-width="isFlat ? 220 : utmLabelMinWidth" :label="$t('ReportBuilder.utm.utmLabel')">
            <template #default="scope">
              <div class="utm-cell" :style="{ paddingLeft: isFlat ? '0' : (scope.row._depth * 20) + 'px' }">
                <template v-if="!isFlat">
                  <span
                    v-if="scope.row.next_level !== '0' && scope.row.next_level !== 0"
                    class="btn-expand"
                    :class="{ loading: isLoading(scope.row) }"
                    @click="toggleExpand(scope.row)"
                  >
                    <span
                      v-if="isLoading(scope.row)"
                      class="spinner-grow spinner-grow-sm"
                      role="status"
                      style="width: 10px; height: 10px;"
                    ></span>
                    <template v-else>{{ isExpanded(scope.row) ? '−' : '+' }}</template>
                  </span>
                  <span v-else class="utm-leaf-spacer"></span>
                </template>
                <span class="utm-name" :title="scope.row.utm_name || $t('ReportBuilder.utm.none')">{{ scope.row.utm_name || $t('ReportBuilder.utm.none') }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            v-for="(req, idx) in metricRequests"
            :key="idx"
            align="right"
            min-width="120"
          >
            <template #header>
              <div class="metric-header">
                <el-tooltip
                  v-if="isUtmTrends"
                  :content="utmActiveMetric === idx ? $t('ReportBuilder.utm.activeOnChart') : $t('ReportBuilder.utm.showOnChart')"
                  placement="top"
                >
                  <span
                    class="utm-chart-toggle"
                    :class="{ active: utmActiveMetric === idx }"
                    @click.stop="setUtmActiveMetric(idx)"
                  >
                    <i class="fas fa-chart-line"></i>
                  </span>
                </el-tooltip>
                <span class="sort-header" @click="switchSort(idx)">
                  <el-tooltip
                    :content="metricTooltip(req, idx)"
                    placement="top"
                    :show-after="300"
                  >
                    <span class="metric-label-text">{{ metricLabel(req) || ('Metric ' + (idx + 1)) }}</span>
                  </el-tooltip>
                  <i
                    v-if="currentSortBy === idx"
                    :class="currentSortDir === 'desc' ? 'fas fa-sort-down' : 'fas fa-sort-up'"
                    class="ms-1"
                    style="font-size: 11px; flex-shrink: 0;"
                  ></i>
                  <i v-else class="fas fa-sort ms-1" style="font-size: 11px; opacity: 0.3; flex-shrink: 0;"></i>
                </span>
              </div>
            </template>
            <template #default="scope">
              <span class="metric-value">{{ formatMetric(scope.row['metric_' + idx]) }}</span>
            </template>
          </el-table-column>

          <!-- Formula columns (computed client-side) -->
          <el-table-column
            v-for="(formula, fi) in formulas"
            :key="'formula_' + fi"
            align="right"
            min-width="120"
          >
            <template #header>
              <div class="metric-header">
                <span class="metric-label-text">{{ formula.label || ('Formula ' + (fi + 1)) }}</span>
                <el-tag size="small" type="warning" effect="plain" class="formula-col-badge">f(x)</el-tag>
              </div>
            </template>
            <template #default="scope">
              <span class="metric-value">{{ formatFormulaValue(evaluateFormula(formula.expression, buildVarMap(scope.row))) }}</span>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="loading && !displayRows.length" class="text-center py-4">
          <span class="spinner-grow spinner-grow-sm text-secondary me-2" role="status"></span>
          {{ $t('General.Loading') }}
        </div>

        <div v-if="!loading && !displayRows.length" class="text-center text-muted py-4">
          {{ $t('ReportBuilder.utm.noData') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HardcodedDateWarning from "./_hardcodedDateWarning.vue"
import { Bar } from 'vue-chartjs'
import 'chart.js/auto'
import ReportBuilderService from "@/helpers/api/reportBuilder.service"
import { useEvents } from "@/composables/useEvents"
import { isUtmTrendsBreakdown } from "@/helpers/reports/utmTrends"
import { UTM_PALETTE } from "@/helpers/reports/palette"

// Bar chart palette (utm_table mode only)
const PALETTE = [
  '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  '#FF9F40', '#8E44AD', '#2ECC71', '#F1C40F', '#E74C3C',
  '#1ABC9C', '#3498DB', '#9B59B6', '#34495E', '#7F8C8D'
]


export default {
  name: "UtmReportView",
  components: { Bar, HardcodedDateWarning },
  inject: {
    injectedReportData: { default: null },
    // In isolated mode (dashboard widget) drill-down payloads must come from the
    // owning Report.vue, not from the global builder store.
    injectedBuildPayload: { default: null }
  },
  setup() {
    const { getEventName: resolveEventName } = useEvents()
    return { resolveEventName }
  },
  data() {
    return {
      cache: {},
      rawCache: {}, // raw flat daily rows per expand-key (for chart)
      expandedKeys: {},
      loadingKeys: {},
      loading: false,
      localSortBy: 0,
      localSortDir: 'desc',
      chartOptions: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true, mode: 'index', intersect: false }
        },
        scales: {
          x: { beginAtZero: true, ticks: { precision: 0 } },
          y: { ticks: { font: { size: 12 } } }
        }
      }
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    hiddenDatasets() {
      return this.reportData.hiddenDatasets || []
    },
    isUtmTrends() {
      return isUtmTrendsBreakdown(this.reportData)
    },
    rows() {
      const data = Array.isArray(this.reportData.chartData) ? this.reportData.chartData : []

      if (this.isUtmTrends && data.length > 0 && data[0].day_start !== undefined) {
        const numMetrics = (this.reportData.source_request?.requests || []).length || 1
        const result = this.aggregateUtmTrendsRows(data, numMetrics)
        const sortKey = `metric_${this.localSortBy}`
        const dir = this.localSortDir === 'asc' ? 1 : -1
        result.sort((a, b) => ((Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0)) * dir)
        return result
      }

      return data
    },
    metricRequests() {
      return this.reportData.source_request?.requests || []
    },
    formulas() {
      return (this.reportData.source_request?.formulas || []).filter(f => f?.expression)
    },
    utmActiveMetric() {
      return this.reportData.utmActiveMetric || 0
    },
    displayMode() {
      return this.reportData.source_request?.display_mode || 'hierarchical'
    },
    isFlat() {
      return this.displayMode === 'flat'
    },
    currentSortBy() {
      return this.isUtmTrends ? this.localSortBy : (this.reportData.source_request?.sort_by ?? 0)
    },
    currentSortDir() {
      return this.isUtmTrends ? this.localSortDir : (this.reportData.source_request?.sort_dir ?? 'desc')
    },
    limitReached() {
      const limit = this.reportData.source_request?.row_limit || 100
      return this.rows.length >= limit
    },
    displayRows() {
      if (this.isFlat) {
        return this.rows.map(r => ({ ...r, _depth: 0 }))
      }
      return this.buildFlatList(this.rows, 0)
    },
    // Deepest currently-expanded row — the label column grows with it so nested
    // UTM values (whose paddingLeft grows by depth * 20px) don't get clipped.
    maxDepth() {
      return this.displayRows.reduce((max, row) => Math.max(max, row._depth || 0), 0)
    },
    utmLabelMinWidth() {
      return 220 + this.maxDepth * 20
    },
    // Only root-level rows for chart
    rootRows() {
      return this.rows.slice(0, 20)
    },
    chartData() {
      if (!this.rootRows.length) return null
      const labels = this.rootRows.map(r => r.utm_name || 'none')
      const datasets = this.metricRequests.map((req, idx) => ({
        label: this.metricLabel(req) || ('Metric ' + (idx + 1)),
        data: this.rootRows.map(r => Number(r['metric_' + idx]) || 0),
        backgroundColor: PALETTE[idx % PALETTE.length],
        borderRadius: 4,
        barThickness: 18,
      }))
      return { labels, datasets }
    },
    // Map groupKey → chart line color — mirrors the two-pass order in _chart.vue buildUtmTrendsData.
    utmColorMap() {
      if (!this.isUtmTrends) return {}
      const mainData = Array.isArray(this.reportData.chartData) ? this.reportData.chartData : []
      const extraDataMap = this.reportData.utmExpandedRows || {}
      const extraData = Object.values(extraDataMap).flat()
      const metricKey = 'metric_' + this.utmActiveMetric

      const computeTotals = (rows) => {
        const totals = {}
        for (const row of rows) {
          const key = (row.utm_id != null) ? String(row.utm_id) : (row.utm_name || 'none')
          totals[key] = (totals[key] || 0) + (Number(row[metricKey]) || 0)
        }
        return totals
      }

      const topTotals = computeTotals(mainData)
      const childTotals = computeTotals(extraData)
      for (const key of Object.keys(childTotals)) {
        if (topTotals[key]) delete childTotals[key]
      }

      const sortedTop = Object.entries(topTotals).sort(([, a], [, b]) => b - a)
      const sortedChild = Object.entries(childTotals).sort(([, a], [, b]) => b - a)

      const map = {}
      ;[...sortedTop, ...sortedChild].forEach(([key], i) => {
        map[key] = UTM_PALETTE[i % UTM_PALETTE.length]
      })
      return map
    },
    // Cell-level left border — applied to UTM name column (border on <tr> is ignored by browsers)
    // In utm_trends mode: col 0 = checkbox, col 1 = name → border goes on col 1
    // In utm_table mode:  col 0 = name → border goes on col 0
    utmCellStyle() {
      return ({ row, columnIndex }) => {
        if (!this.isUtmTrends) return {}
        const borderColIndex = 1 // checkbox occupies col 0, name is col 1
        if (columnIndex !== borderColIndex) return {}
        const color = this.utmColorMap[this.utmRowKey(row)]
        return color
          ? { borderLeft: `3px solid ${color}` }
          : { borderLeft: '3px solid transparent' }
      }
    }
  },
  watch: {
    'reportData.chartData'() {
      this.cache = {}
      this.rawCache = {}
      this.expandedKeys = {}
      this.loadingKeys = {}
      this.resetExtraData()
      this.applyTop5Visibility()
    }
  },
  methods: {
    // Resolve a single event id to a translated display name.
    // Priority: locale by id → locale by name → useEvents cache → raw name string
    resolveOneEventId(id, fallbackName) {
      if (id === 'all' || id === 0 || id === '0' || id === null || id === undefined) {
        return this.$t('Components.EventSelector.AnyEvent')
      }
      const idKey = `Components.EventSelector.CustomEvents.${id}.name`
      if (this.$te(idKey)) return this.$t(idKey)
      if (fallbackName) {
        const nameKey = `Components.EventSelector.CustomEvents.${fallbackName}.name`
        if (this.$te(nameKey)) return this.$t(nameKey)
      }
      // useEvents cache (resolveEventName returns raw name or stringified id)
      const cached = this.resolveEventName(id)
      if (cached && cached !== String(id)) return cached
      return fallbackName || String(id)
    },
    // Map agr value → translated short label
    agrLabel(agr) {
      const map = {
        total:                    'ReportBuilder.action.total',
        total_uniq_user_id:       'ReportBuilder.action.uniqueUsers',
        wau:                      'ReportBuilder.action.wau',
        mau:                      'ReportBuilder.action.mau',
        total_uniq_session_id:    'ReportBuilder.action.uniqueSessions',
        total_uniq_app_session_id:'ReportBuilder.action.uniqueAppSessions',
        avg_user_id:              'ReportBuilder.action.avg',
        min_user_id:              'ReportBuilder.action.min',
        max_user_id:              'ReportBuilder.action.max',
        median_user_id:           'ReportBuilder.action.median',
        property_sum:             'ReportBuilder.action.sum',
        property_avg:             'ReportBuilder.action.avg',
        property_min:             'ReportBuilder.action.min',
        property_max:             'ReportBuilder.action.max',
        property_median:          'ReportBuilder.action.median',
      }
      const key = map[agr]
      return key ? this.$t(key) : agr || ''
    },
    // Tooltip content: full event name + aggregation
    metricTooltip(req, idx) {
      if (!req) return ''
      const name = this.metricLabel(req) || ('Metric ' + (idx + 1))
      let agr = this.agrLabel(req.agr)
      if (agr && req?.agr_field === 'events.value_amount') {
        const currency = this.$store.state.init.user?.report_currency
        if (currency) agr = `${agr}, ${currency}`
      }
      return agr ? `${name}\n${agr}` : name
    },
    // Full label resolution for a metric request object
    metricLabel(req) {
      if (!req) return null
      // 1. Custom user label
      if (req.label) return req.label
      // 2. Normalise event_id to array
      const ids = Array.isArray(req.event_id)
        ? req.event_id
        : (req.event_id != null ? [req.event_id] : [])
      // 3. "Any event" shortcut
      if (!ids.length || ids.some(id => id === 'all' || id === 0 || id === '0' || id === null)) {
        return this.$t('Components.EventSelector.AnyEvent')
      }
      // 4. Resolve each id with locale lookup, fallback to stored event_name fragment
      const nameParts = req.event_name ? req.event_name.split(', ') : []
      const labels = ids.map((id, i) => this.resolveOneEventId(id, nameParts[i]))
      return labels.join(', ') || req.event_name || null
    },
    aggregateUtmTrendsRows(rawData, numMetrics, parentPath = null) {
      const aggregated = {}
      for (const row of rawData) {
        const key = row.utm_id
        if (!aggregated[key]) {
          const init = {
            utm_name: row.utm_name,
            utm_id: row.utm_id,
            next_level: 0,
            current_level: parentPath ? [...parentPath, row.utm_id] : [row.utm_id],
          }
          for (let i = 0; i < numMetrics; i++) init[`metric_${i}`] = 0
          aggregated[key] = init
        }
        // next_level: take max across all day rows for this utm_id
        const rowNextLevel = Number(row.next_level) || 0
        if (rowNextLevel > aggregated[key].next_level) {
          aggregated[key].next_level = rowNextLevel
        }
        for (let i = 0; i < numMetrics; i++) {
          aggregated[key][`metric_${i}`] += Number(row[`metric_${i}`]) || 0
        }
      }
      return Object.values(aggregated)
    },
    // Returns the same unique key that buildUtmTrendsData uses for hiddenDatasets.
    // Prefer utm_id (always unique) over utm_name (can be empty/duplicate).
    utmRowKey(row) {
      if (row.utm_id !== undefined && row.utm_id !== null) return String(row.utm_id)
      return row.utm_name || 'none'
    },
    // On new data load: show only the top-5 UTMs by metric total, hide the rest.
    applyTop5Visibility() {
      if (!this.isUtmTrends) return
      const data = Array.isArray(this.reportData.chartData) ? this.reportData.chartData : []
      if (!data.length) return
      const metricKey = 'metric_' + (this.reportData.utmActiveMetric || 0)
      const totals = {}
      for (const row of data) {
        const key = (row.utm_id !== undefined && row.utm_id !== null)
          ? String(row.utm_id)
          : (row.utm_name || 'none')
        totals[key] = (totals[key] || 0) + (Number(row[metricKey]) || 0)
      }
      const sortedKeys = Object.entries(totals)
        .sort(([, a], [, b]) => b - a)
        .map(([key]) => key)
      // Keys beyond position 4 (5th+) start hidden
      const hiddenKeys = sortedKeys.slice(5)
      if (this.injectedReportData) {
        this.injectedReportData.hiddenDatasets = hiddenKeys
      } else {
        this.$store.commit('ReportFromBuilder/setHiddenDatasets', hiddenKeys)
      }
    },
    // Appends keys to hiddenDatasets without touching ones already there (either
    // hidden or explicitly left visible by the user).
    hideDatasets(keys) {
      const merged = [...this.hiddenDatasets, ...keys]
      if (this.injectedReportData) {
        this.injectedReportData.hiddenDatasets = merged
      } else {
        this.$store.commit('ReportFromBuilder/setHiddenDatasets', merged)
      }
    },
    toggleVisibility(key) {
      if (this.injectedReportData) {
        if (!Array.isArray(this.injectedReportData.hiddenDatasets)) {
          this.injectedReportData.hiddenDatasets = []
        }
        const arr = this.injectedReportData.hiddenDatasets
        const pos = arr.indexOf(key)
        if (pos === -1) arr.push(key)
        else arr.splice(pos, 1)
      } else {
        this.$store.commit('ReportFromBuilder/toggleDatasetVisibility', key)
      }
    },
    setUtmActiveMetric(idx) {
      if (this.injectedReportData) {
        this.injectedReportData.utmActiveMetric = idx
      } else {
        this.$store.commit('ReportFromBuilder/setUtmActiveMetric', idx)
      }
    },
    // Chart extra-data lives in Vuex for the builder and in the injected local
    // state for dashboard widgets — route writes to whichever one is in use.
    setExtraData(key, rows) {
      if (this.injectedReportData) {
        this.injectedReportData.utmExpandedRows = {
          ...(this.injectedReportData.utmExpandedRows || {}), [key]: rows
        }
      } else {
        this.$store.commit('ReportFromBuilder/setUtmExpandedRows', { key, rows })
      }
    },
    removeExtraData(key) {
      if (this.injectedReportData) {
        const next = { ...(this.injectedReportData.utmExpandedRows || {}) }
        delete next[key]
        this.injectedReportData.utmExpandedRows = next
      } else {
        this.$store.commit('ReportFromBuilder/removeUtmExpandedRows', key)
      }
    },
    resetExtraData() {
      if (this.injectedReportData) {
        this.injectedReportData.utmExpandedRows = {}
      } else {
        this.$store.commit('ReportFromBuilder/resetUtmExpandedRows')
      }
    },
    rowKey(row) {
      if (Array.isArray(row.current_level)) {
        return row.current_level.join('_')
      }
      return row.utm_id + '_' + row.utm_name
    },

    buildFlatList(rows, depth) {
      const result = []
      if (!rows) return result
      for (const row of rows) {
        const enriched = { ...row, _depth: depth }
        result.push(enriched)
        const key = this.rowKey(row)
        if (this.expandedKeys[key] && this.cache[key]) {
          const children = this.buildFlatList(this.cache[key], depth + 1)
          result.push(...children)
        }
      }
      return result
    },

    isExpanded(row) {
      return !!this.expandedKeys[this.rowKey(row)]
    },

    isLoading(row) {
      return !!this.loadingKeys[this.rowKey(row)]
    },

    async toggleExpand(row) {
      const key = this.rowKey(row)

      if (this.expandedKeys[key]) {
        // Collapse: hide the row and remove its children's chart data
        delete this.expandedKeys[key]
        this.expandedKeys = { ...this.expandedKeys }
        if (this.isUtmTrends) {
          this.removeExtraData(key)
        }
        return
      }

      if (this.cache[key]) {
        // Already cached — just expand and restore chart data if needed
        if (this.isUtmTrends && this.rawCache[key]) {
          this.setExtraData(key, this.rawCache[key])
        }
        this.expandedKeys = { ...this.expandedKeys, [key]: true }
        return
      }

      this.loadingKeys = { ...this.loadingKeys, [key]: true }

      try {
        const payload = this.injectedBuildPayload
          ? this.injectedBuildPayload()
          : JSON.parse(JSON.stringify(this.$store.getters['ReportFromBuilder/reportConfig']))

        if (!payload.source_request) payload.source_request = {}

        payload.source_request.drill_path = Array.isArray(row.current_level)
          ? [...row.current_level]
          : row.current_level

        if (payload.settings?.params?.[':resource_key']) {
          let rKeys = payload.settings.params[':resource_key']
          if (Array.isArray(rKeys)) {
            const currentBotKey = this.$store.state.init?.current_bot?.key
            payload.settings.params[':resource_key'] = rKeys.map(k =>
              k === '$current_resource' ? currentBotKey : k
            ).filter(Boolean)
          }
        }

        const data = await ReportBuilderService.useReportTemplate({}, payload)
        let resultData = Array.isArray(data) ? data : (data?.data || [])

        // Save raw flat rows for chart BEFORE aggregation
        const hasDailyRows = resultData.length > 0 && resultData[0].day_start !== undefined
        if (this.isUtmTrends && hasDailyRows) {
          this.rawCache = { ...this.rawCache, [key]: resultData }
          this.setExtraData(key, resultData)

          const numMetrics = (this.reportData.source_request?.requests || []).length || 1
          const parentPath = Array.isArray(row.current_level) ? row.current_level : [row.utm_id]
          resultData = this.aggregateUtmTrendsRows(resultData, numMetrics, parentPath)

          // Newly revealed children start unchecked — expanding a row must not change
          // what was already selected on the chart/legend (e.g. the initial top-5).
          const newlyHidden = resultData
            .map(r => this.utmRowKey(r))
            .filter(k => !this.hiddenDatasets.includes(k))
          if (newlyHidden.length) {
            this.hideDatasets(newlyHidden)
          }
        }

        this.cache = { ...this.cache, [key]: resultData }
        this.expandedKeys = { ...this.expandedKeys, [key]: true }
      } catch (e) {
        console.error('UTM drill-down error:', e)
      } finally {
        delete this.loadingKeys[key]
        this.loadingKeys = { ...this.loadingKeys }
      }
    },

    switchSort(idx) {
      if (this.isUtmTrends) {
        if (this.localSortBy === idx) {
          this.localSortDir = this.localSortDir === 'desc' ? 'asc' : 'desc'
        } else {
          this.localSortBy = idx
          this.localSortDir = 'desc'
        }
        return
      }
      const store = this.$store
      if (this.currentSortBy === idx) {
        store.commit('ReportFromBuilder/setSortDir', this.currentSortDir === 'desc' ? 'asc' : 'desc')
      } else {
        store.commit('ReportFromBuilder/setSortBy', idx)
        store.commit('ReportFromBuilder/setSortDir', 'desc')
      }
      this.cache = {}
      this.expandedKeys = {}
      store.dispatch('ReportFromBuilder/loadDataForReport')
    },

    formatMetric(val) {
      if (val === null || val === undefined) return '0'
      if (typeof val === 'number') {
        return val % 1 !== 0 ? val.toFixed(2) : val.toLocaleString()
      }
      return val
    },
    buildVarMap(row) {
      const map = {}
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const numMetrics = this.metricRequests.length
      for (let i = 0; i < numMetrics; i++) {
        map[letters[i]] = Number(row['metric_' + i]) || 0
      }
      return map
    },
    evaluateFormula(expression, varMap) {
      if (!expression || !/^[A-Za-z0-9+\-*/().%\s]+$/.test(expression)) return null
      let expr = expression.toUpperCase()
      for (const [letter, value] of Object.entries(varMap)) {
        const safeVal = (value === null || value === undefined || isNaN(value)) ? 0 : value
        expr = expr.replace(new RegExp(`\\b${letter}\\b`, 'g'), `(${safeVal})`)
      }
      try {
        // eslint-disable-next-line no-new-func
        const result = new Function(`return ${expr}`)()
        if (!isFinite(result)) return null
        return Math.round(result * 10000) / 10000
      } catch {
        return null
      }
    },
    formatFormulaValue(val) {
      if (val === null || val === undefined) return '—'
      return val % 1 !== 0 ? val.toFixed(2) : val.toLocaleString()
    }
  }
}
</script>

<style scoped>
.rb-trends-table-wrapper {
  background: var(--ui-bg-card, #fff);
  border-radius: 12px;
  border: 1px solid var(--ui-border, #e4e7ed);
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* utm_trends in a dashboard widget: this component is a flex sibling of _chart.vue
   (see Report.vue), both stretched via Bootstrap's flex-grow-1. Without a matching
   flex-basis: 0 here, this table's natural (auto) content height wins the flex-basis
   race against the chart's own flex-basis: 0 (.main-chart--embedded) — so the table
   renders full height (pushing the widget's scroll area) while the chart gets
   shrunk to its floor. Mirrors the chart's own embedded treatment so extra height
   from resizing the widget is shared, and the table scrolls internally instead. */
.utm-report-embedded {
  flex: 1 1 0;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.utm-report-embedded .rb-trends-table-wrapper {
  flex: 1 1 auto;
  min-height: 0;
}

/* Embedded in a dashboard widget: no nested card chrome, tighter spacing, and the
   heading collapses to a small uppercase caption instead of a full h5 — mirrors
   .rb-compact in _trends_table.vue. */
.rb-compact.rb-trends-table-wrapper {
  border: none;
  border-radius: 0;
  padding: 0;
}

.rb-compact .d-flex.mb-3 {
  margin-bottom: 8px !important;
}

.rb-compact .rb-trends-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ui-text-3, #909399);
}

.rb-compact :deep(.el-table) {
  font-size: 12px;
}

.rb-compact :deep(.el-table .cell) {
  padding-left: 6px;
  padding-right: 6px;
  line-height: 1.3;
}

.rb-compact :deep(.el-table td.el-table__cell),
.rb-compact :deep(.el-table th.el-table__cell) {
  padding: 4px 0;
}

.rb-compact :deep(.el-table th.el-table__cell) {
  font-size: 11px;
  font-weight: 600;
}

.rb-compact .utm-name,
.rb-compact .metric-value {
  font-size: 12px;
}

.rb-table-container {
  overflow-x: auto;
  overflow-y: auto;
  flex-grow: 1;
}

.utm-cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.utm-leaf-spacer {
  display: inline-block;
  width: 16px;
  margin-right: 4px;
  flex-shrink: 0;
}

.utm-name {
  font-size: 13px;
  color: var(--ui-text-1, #303133);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.btn-expand {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  margin-right: 6px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: #909399;
  background: var(--ui-bg-page, #f5f7fa);
  border: 1px solid var(--ui-border, #e4e7ed);
  user-select: none;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}

.btn-expand:hover {
  color: #409EFF;
  background: rgba(64, 158, 255, 0.08);
  border-color: rgba(64, 158, 255, 0.3);
}

.btn-expand.loading {
  animation: utm-btn-pulse 0.8s ease-in-out infinite alternate;
}

@keyframes utm-btn-pulse {
  from { opacity: 1; }
  to   { opacity: 0.35; }
}

.sort-header {
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.sort-header:hover {
  color: #409eff;
}

.metric-label-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.metric-value {
  font-size: 13px;
  color: var(--ui-text-1, #303133);
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
  min-width: 0;
  overflow: hidden;
}

.utm-chart-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  flex-shrink: 0;
  cursor: pointer;
  color: #c0c4cc;
  background: transparent;
  transition: color 0.15s, background 0.15s;
  font-size: 11px;
}

.utm-chart-toggle:hover {
  color: #409EFF;
  background: rgba(64, 158, 255, 0.1);
}

.utm-chart-toggle.active {
  color: #409EFF;
  background: rgba(64, 158, 255, 0.12);
}

.formula-col-badge {
  font-size: 10px;
  height: 18px;
  line-height: 16px;
  padding: 0 4px;
  flex-shrink: 0;
}

</style>

